import React, { useContext, useMemo } from 'react'
import { KTCard } from '../../../../_metronic/helpers/components/KTCard'
import { KTSVG } from '../../../../_metronic/helpers'
import { Grant, GrantStatus } from '../../../../generated/graphql'
import { formatNumber } from '../../../../utils/CurrencyFormatter'
import { GlobalContext } from '../../store/context/globalContext'

type Props = {
  grants: Grant[]
}

type PortfolioStat = {
  title: string
  value: string
  subtext: string
  className: string
  badgeClass: string
  iconPath?: string
}

export const PortfolioOverviewCard: React.FC<Props> = ({ grants }) => {
  const { state } = useContext(GlobalContext)

  const stats: PortfolioStat[] = useMemo(() => {
    const totalCompanies = new Set(grants.map((g) => g.companyName?.trim()).filter(Boolean)).size
    const totalGrants = grants.length
    const totalOptionsGranted = grants.reduce((acc, g) => acc + (g.numberGranted ?? 0), 0)

    const vestedOptions = grants.reduce((acc, g) => {
      if (g.vestedOptions != null) {
        return acc + (g.vestedOptions ?? 0)
      }

      const grantDate = g.grantDate ? new Date(g.grantDate) : null
      const currentDate = new Date('2025-08-07')
      const vestingPeriodYears = 4
      const cliffYears = 1
      const numberGranted = g.numberGranted ?? 0

      if (!grantDate || g.status !== 'Active') {
        return acc
      }

      const yearsElapsed =
        (currentDate.getTime() - grantDate.getTime()) / (1000 * 60 * 60 * 24 * 365)
      if (yearsElapsed < cliffYears) {
        return acc
      }

      const vestedFraction = Math.min(yearsElapsed / vestingPeriodYears, 1)
      const vested = Math.floor(numberGranted * vestedFraction)
      return acc + vested
    }, 0)

    const exercisedOptions = grants.reduce((acc, g) => {
      const active = g.numberExercised ?? 0
      return acc + active
    }, 0)

    const activeGrantCompanies = grants
      .filter((obj) => obj.status === GrantStatus.Active)
      .map((g) => g.companyName)

    const activeCompanies = Array.from(new Set(activeGrantCompanies))
    return [
      {
        title: 'Total Companies',
        value: totalCompanies.toString(),
        subtext: `${formatNumber({ value: activeCompanies.length })} active company grants`,
        className: '',
        badgeClass: 'blue-heading',
        iconPath: '/media/custom-images/company.svg',
      },
      {
        title: 'Total Grants',
        value: totalGrants.toString(),
        subtext: `${formatNumber({ value: totalGrants })} granted`,
        className: '',
        badgeClass: 'blue-heading',
        iconPath: '/media/custom-images/grant.svg',
      },
      {
        title: 'Total Vested',
        value: vestedOptions > 0 ? vestedOptions.toLocaleString() : '0',
        subtext: `${
          totalOptionsGranted > 0 ? ((vestedOptions / totalOptionsGranted) * 100).toFixed(1) : '0.0'
        }% of total granted`,
        className: '',
        badgeClass: 'info-active',
        iconPath: '/media/custom-images/vested.svg',
      },
      {
        title: 'Total Exercised',
        value: exercisedOptions > 0 ? exercisedOptions.toLocaleString() : '0',
        subtext: `${
          vestedOptions > 0 ? ((exercisedOptions / vestedOptions) * 100).toFixed(1) : '0.0'
        }% of vested`,
        className: '',
        badgeClass: 'info-active',
        iconPath: '/media/custom-images/exercised.svg',
      },
    ]
  }, [grants])

  return (
    <div className='card border border-gray-300 p-6 p-md-8 mb-5 mb-md-0'>
      <div className='card-header px-0'>
        <h3 className='card-title flex-column'>
          <span className='card-label gap-2 mb-2 fs-4 fs-md-3'>Portfolio Summary</span>
          <small className='text-gray-700 fs-7 fs-md-6'>
            Overview of your equity grants across all companies
          </small>
        </h3>
      </div>

      <div className='card-body px-0'>
        <div className='row g-4 g-md-5'>
          {stats.map((item, i) => (
            <PortfolioStatCard key={i} item={item} />
          ))}
        </div>
      </div>
    </div>
  )
}

const PortfolioStatCard: React.FC<{ item: PortfolioStat }> = ({ item }) => {
  return (
    <div className='col-12 col-md-4 col-lg-3 mb-5 mb-md-0'>
      <div
        className={`card border border-gray-300 h-100 hover:shadow-sm transition-shadow ${item.className}`}
      >
        <div className='card-body p-4 p-md-5'>
          <div className='d-flex justify-content-start align-items-center mb-1 gap-2 gap-md-3'>
            <div className='fw-bold fs-6 fs-md-5'>{item.title}</div>
            {item.iconPath && (
              <div className='card-icon'>
                <KTSVG path={item.iconPath} className='svg-icon-2 svg-icon-md-1 text-gray-700' />
              </div>
            )}
          </div>
          <div className='fs-3 fs-md-2x fw-bold text-dark mb-2'>{item.value}</div>
          <div className='fs-8 fs-md-7'>
            {item.title === 'Total Companies' || item.title === 'Total Grants' ? (
              <span className='text-gray-700'>{item.subtext}</span>
            ) : (
              <span
                className='badge'
                style={{
                  border: '1px solid #c8dbfc',
                  backgroundColor: '#ebf2fe',
                  borderRadius: '50px',
                  color: '#2661e2ff',
                  fontWeight: 500,
                  padding: '0.25rem 0.75rem',
                  fontSize: '0.75rem',
                }}
              >
                {item.subtext}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
